/**
 * ScrapListPage — Selejt tetelek listazasa
 * Epic 65: Selejtezés & Készletkorrekció
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useCreateScrapRecord, useScrapRecords } from '@/hooks/useScrap';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

const REASON_LABELS: Record<string, string> = {
  DAMAGED: 'inventory.scrapReasonLabels.damaged',
  EXPIRED: 'inventory.scrapReasonLabels.expired',
  DEFECTIVE: 'inventory.scrapReasonLabels.defective',
  OTHER: 'inventory.scrapReasonLabels.other',
};

export function ScrapListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { result, isLoading, error } = useScrapRecords();
  const createRecord = useCreateScrapRecord();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    inventoryItemId: '',
    productId: '',
    warehouseId: '',
    quantity: '',
    reason: 'DAMAGED' as const,
    note: '',
  });

  const handleCreate = async () => {
    const qty = parseInt(form.quantity, 10);
    if (!form.inventoryItemId || !form.productId || !form.warehouseId || !qty || qty <= 0) return;

    await createRecord.mutateAsync({
      inventoryItemId: form.inventoryItemId,
      productId: form.productId,
      warehouseId: form.warehouseId,
      quantity: qty,
      reason: form.reason,
      note: form.note || undefined,
    });

    setForm({
      inventoryItemId: '',
      productId: '',
      warehouseId: '',
      quantity: '',
      reason: 'DAMAGED',
      note: '',
    });
    setShowForm(false);
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('inventory.scrapItems')}</h1>
        <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('inventory.scrapItems')}</h1>
        <Card>
          <CardContent className="p-8 text-center text-red-500">
            {t('page.errorPrefix', { message: error })}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('inventory.scrapItems')}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/inventory/scrap/protocols')}>
            {t('inventory.scrapProtocols')}
          </Button>
          <Button onClick={() => setShowForm(!showForm)}>
            {showForm ? t('common.cancel') : t('inventory.newScrap')}
          </Button>
        </div>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('inventory.scrapMark')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Input
                placeholder={t('inventory.inventoryItemId')}
                value={form.inventoryItemId}
                onChange={e => setForm({ ...form, inventoryItemId: e.target.value })}
              />
              <Input
                placeholder={t('inventory.productId')}
                value={form.productId}
                onChange={e => setForm({ ...form, productId: e.target.value })}
              />
              <Input
                placeholder={t('inventory.warehouseId')}
                value={form.warehouseId}
                onChange={e => setForm({ ...form, warehouseId: e.target.value })}
              />
              <Input
                type="number"
                placeholder={t('common.quantity')}
                value={form.quantity}
                onChange={e => setForm({ ...form, quantity: e.target.value })}
              />
              <select
                className="border rounded px-3 py-2 bg-background"
                value={form.reason}
                onChange={e => setForm({ ...form, reason: e.target.value as typeof form.reason })}
              >
                <option value="DAMAGED">{t('inventory.scrapReasonLabels.damaged')}</option>
                <option value="EXPIRED">{t('inventory.scrapReasonLabels.expired')}</option>
                <option value="DEFECTIVE">{t('inventory.scrapReasonLabels.defective')}</option>
                <option value="OTHER">{t('inventory.scrapReasonLabels.other')}</option>
              </select>
              <Input
                placeholder={t('common.notes')}
                value={form.note}
                onChange={e => setForm({ ...form, note: e.target.value })}
              />
            </div>
            <Button className="mt-3" onClick={handleCreate} disabled={createRecord.isPending}>
              {createRecord.isPending ? t('inventory.recording') : t('inventory.scrapRecord')}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('inventory.scrapRecords', { count: result?.total ?? 0 })}</CardTitle>
        </CardHeader>
        <CardContent>
          {!result?.records.length ? (
            <p className="text-muted-foreground text-center py-4">
              {t('inventory.noScrapRecords')}
            </p>
          ) : (
            <div className="divide-y">
              {result.records.map(record => (
                <div key={record.id} className="py-3 flex justify-between items-center">
                  <div>
                    <span className="font-medium">{record.productId}</span>
                    <span className="ml-3 text-sm text-muted-foreground">
                      {t(REASON_LABELS[record.reason] ?? '') || record.reason}
                    </span>
                    <span className="ml-2 text-sm text-muted-foreground">x{record.quantity}</span>
                    {record.note && (
                      <span className="ml-2 text-sm text-muted-foreground">— {record.note}</span>
                    )}
                  </div>
                  <div className="text-right">
                    {record.totalValue !== undefined && (
                      <span className="font-medium text-red-600">
                        {formatCurrency(record.totalValue)}
                      </span>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {new Date(record.createdAt).toLocaleDateString('hu-HU')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
